import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { SelectChangeEvent, Box, Stack } from "@mui/material"

import { CreateOrderDialogMode } from "../../createOrder/createOrderTypes"
import { useOrderDialogUrlState } from "../../createOrder/hooks/useOrderDialogUrlState"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { CreditReferencesTable } from "./components/CreditReferencesTable"
import {
    fetchOrderDetails,
    OrderDetailsResponse,
    MeetingServiceQuery,
} from "../../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { CreditNoteTypeSelect } from "./components/CreditNoteTypeSelect"
import { CreditNoteType, mapCreditTypeToDialogType } from "./helpers"
import { StandardAlertInfo } from "../../alert/StandardAlertInfo"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { StandardButton } from "../../buttons/StandardButton"
import { StandardDialog } from "../StandardDialog"

const useStyles = makeStyles()(() => ({
    dialogPaper: { padding: "3rem" },
    typeSelect: { minWidth: "27rem" },
    alertContainer: { height: "8rem", padding: "2rem 0" },
    buttonStack: { marginTop: "8.375rem", justifyContent: "center", gap: "2rem" },
    actionButton: { width: "7.5rem" },
}))

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

type CreateCreditNoteDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    orderId: number
}>

export function CreateCreditNoteDialog({ open, onClose, orderId }: CreateCreditNoteDialogProps) {
    const { classes } = useStyles()
    const [selectedType, setSelectedType] = useState<CreditNoteType>(CreditNoteType.CREDIT)

    const { openDialog } = useOrderDialogUrlState()

    const { data: orderDetails, error } = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: { orderId },
        enabled: Boolean(orderId),
        select: transformData,
    })

    const handleChange = (event: SelectChangeEvent<CreditNoteType>) => {
        setSelectedType(event.target.value as CreditNoteType)
    }

    const references = orderDetails?.accountingDetails?.references ?? []

    const handleNext = () => {
        openDialog({
            orderId,
            mode: CreateOrderDialogMode.EDIT,
            dialogType: mapCreditTypeToDialogType(selectedType),
        })

        onClose()
    }

    return (
        <LocalizedStrict id="production-credit-note-dialog-title" attrs={{ dialogTitle: true }}>
            <StandardDialog
                open={open}
                maxWidthProp="xl"
                onClose={onClose}
                dialogTitle="Create credit note"
                subtitle={
                    orderDetails &&
                    `#${orderDetails?.id}, ${orderDetails?.meetingDetails.meetingName}, ${orderDetails?.customer.givenName} ${orderDetails?.customer.surname}`
                }
            >
                <Stack className={classes.dialogPaper}>
                    <Box>
                        <CreditNoteTypeSelect
                            value={selectedType}
                            onChange={handleChange}
                            className={classes.typeSelect}
                        />
                    </Box>

                    {selectedType && (
                        <Box className={classes.alertContainer}>
                            <LocalizedStrict
                                id={
                                    selectedType === CreditNoteType.CREDIT
                                        ? "production-credit-note-credit-info"
                                        : "production-credit-note-debit-info"
                                }
                                attrs={{ title: true }}
                            >
                                <StandardAlertInfo
                                    title={
                                        selectedType === CreditNoteType.CREDIT
                                            ? "A credit note is used to reverser parts of a booked order."
                                            : "A supplementary order is used to add items to an order that is already booked."
                                    }
                                />
                            </LocalizedStrict>
                        </Box>
                    )}

                    {!!references.length && <CreditReferencesTable references={references} />}

                    <Stack direction="row" className={classes.buttonStack}>
                        <LocalizedStrict id="production-credit-note-button-cancel">
                            <StandardButton
                                variant="outlined"
                                fullWidth={false}
                                onClick={onClose}
                                type="button"
                                className={classes.actionButton}
                            >
                                Close
                            </StandardButton>
                        </LocalizedStrict>

                        <LocalizedStrict id="production-credit-note-button-next">
                            <StandardButton
                                fullWidth={false}
                                onClick={handleNext}
                                disabled={!!error}
                                type="button"
                                className={classes.actionButton}
                            >
                                Next
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
